import {
    lazyAddAttachmentToDraftWeb,
    lazyCreateDraftWeb,
    lazySaveDraftWeb,
    lazySaveSmartReplyWeb,
    lazySendDraftWeb,
    lazySendSmartReplyWeb,
} from 'outlook-mail-compose-web';
import {
    lazyConversationRowByIdWeb,
    lazyConversationRowsWeb,
    lazySubscribeToRowNotificationsWeb,
} from 'outlook-listview-web';
import {
    lazyDeleteConversationWeb,
    lazyUndoDeleteConversationWeb,
} from 'deleteconversation-mutation-web';
import {
    lazyFolderHierarchyWeb,
    lazyFolderWeb,
    lazyFoldersWeb,
    lazySubscribeToHierarchyNotificationsWeb,
    lazyUpdateFolderPermissionsWeb,
} from 'outlook-folders-web';
import { lazyMarkItemAsJunkWeb, lazyUndoMarkItemAsJunkWeb } from 'markasjunkitem-mutation-web';
import {
    lazyMoveConversationWeb,
    lazyUndoMoveConversationWeb,
} from 'moveconversation-mutation-web';
import {
    lazyScheduleConversationWeb,
    lazyUndoScheduleConversationWeb,
} from 'scheduleconversation-mutation-web';

import { Resolvers } from 'owa-graph-schema';
import { lazyAppBarLaunchTilesQueryWeb } from 'appbarlaunchtiles-query-web';
import { lazyCalendarGroupsQueryWeb } from 'calendar-groups-query-web';
import { lazyCategorizeConversationWeb } from 'categorizeconversation-mutation-web';
import { lazyCategorizeItemWeb } from 'categorizeitem-mutation-web';
import { lazyConversationConversationNodesWebResolver } from 'conversation-conversation-nodes-web';
import { lazyCreateCalendarGroupMutationWeb } from 'createcalendargroup-mutation-web';
import { lazyCreateCalendarMutationWeb } from 'createcalendar-mutation-web';
import { lazyCreateFolderWeb } from 'create-folder-mutation-web';
import { lazyDeleteAttachmentMutationWeb } from 'deleteattachment-mutation-web';
import { lazyDeleteCalendarMutationWeb } from 'deletecalendar-mutation-web';
import { lazyDeleteFolderWeb } from 'delete-folder-mutation-web';
import { lazyEmptyViewMutationWeb } from 'empty-view-mutation-web';
import { lazyIgnoreConversationMutationWeb } from 'ignoreconversation-mutation-web';
import { lazyItemRowsWeb } from 'itemrows-query-web';
import { lazyMarkConversationAsReadWeb } from 'markasreadconversation-mutation-web';
import { lazyMarkItemAsPhishingWeb } from 'markasphishingitem-mutation-web';
import { lazyMarkItemAsReadWeb } from 'markasreaditem-mutation-web';
import { lazyMarkViewAsReadMutationWeb } from 'mark-view-as-read-mutation-web';
import { lazyMoveFolderWeb } from 'move-folder-mutation-web';
import { lazyPinConversationMutationWeb } from 'pinconversation-mutation-web';
import { lazyPinItemMutationWeb } from 'pinitem-mutation-web';
import { lazyQueryConversationNodeWeb } from 'query-conversation-node-web';
import { lazyQueryConversationWeb } from 'query-conversation-web';
import { lazyQueryItemWeb } from 'query-item-web';
import { lazyQueryItemsWeb } from 'query-items-web';
import { lazyRenameCalendarGroupMutationWeb } from 'renamecalendargroup-mutation-web';
import { lazyRenameFolderWeb } from 'rename-folder-mutation-web';
import { lazyRespondToMeetingInviteMutationWeb } from 'respondtomeetinginvite-mutation-web';
import { lazySampleWeb } from 'sample-query-web';
import { lazySetInferenceClassificationOnConversationWeb } from 'setinferenceclassificationconversation-mutation-web';
import { lazySubscribeToNotificationChannelReadySubscriptionWeb } from 'subscribetonotificationchannelready-subscription-web';
import { lazyUnsubscribeEmailWeb } from 'itemunsubscribeemail-mutation-web';
import { lazyUpdateLaunchTilePinStatusMutationWeb } from 'updatelaunchtilepinstatus-mutation-web';
import { lazyUpdateUserConfigurationWeb } from 'userconfiguration-mutation-web';
import { lazyUserConfigurationWeb } from 'userconfiguration-query-web';

/**
 * Please keep fields alphebatized to minimize merge conflicts
 */
export const webResolvers: Resolvers = {
    /* ======================== */
    /* Resolvers for root types */
    /* ======================== */

    /**
     * The root query type. All queries that fetch data start at the Query type.
     * Resolvers under Query should not have effects
     *
     * See https://graphql.org/learn/schema/#the-query-and-mutation-types
     */
    Query: {
        appBarLaunchTiles: lazyAppBarLaunchTilesQueryWeb,
        conversation: lazyQueryConversationWeb,
        conversationNode: lazyQueryConversationNodeWeb,
        conversationRowById: lazyConversationRowByIdWeb,
        conversationRows: lazyConversationRowsWeb,
        folder: lazyFolderWeb,
        folderHierarchy: lazyFolderHierarchyWeb,
        folders: lazyFoldersWeb,
        item: lazyQueryItemWeb,
        items: lazyQueryItemsWeb,
        itemRows: lazyItemRowsWeb,
        sample: lazySampleWeb,
        userConfiguration: lazyUserConfigurationWeb,

        /**Calendar queries */
        calendarGroups: lazyCalendarGroupsQueryWeb,
    },

    /**
     * The root mutation type. All queries that alter data start at the Mutation type.
     * Mutations typically return the mutated data.
     *
     * See https://graphql.org/learn/schema/#the-query-and-mutation-types
     */
    Mutation: {
        addAttachmentToDraft: lazyAddAttachmentToDraftWeb,
        categorizeConversation: lazyCategorizeConversationWeb,
        categorizeItem: lazyCategorizeItemWeb,
        createCalendar: lazyCreateCalendarMutationWeb,
        createCalendarGroup: lazyCreateCalendarGroupMutationWeb,
        createDraft: lazyCreateDraftWeb,
        createFolder: lazyCreateFolderWeb,
        deleteAttachmentFromDraft: lazyDeleteAttachmentMutationWeb,
        deleteCalendar: lazyDeleteCalendarMutationWeb,
        deleteFolder: lazyDeleteFolderWeb,
        deleteConversation: lazyDeleteConversationWeb,
        emptyView: lazyEmptyViewMutationWeb,
        ignoreConversation: lazyIgnoreConversationMutationWeb,
        markConversationAsRead: lazyMarkConversationAsReadWeb,
        markItemAsRead: lazyMarkItemAsReadWeb,
        markItemAsJunk: lazyMarkItemAsJunkWeb,
        markItemAsPhishing: lazyMarkItemAsPhishingWeb,
        markViewAsRead: lazyMarkViewAsReadMutationWeb,
        moveConversation: lazyMoveConversationWeb,
        moveFolder: lazyMoveFolderWeb,
        pinConversation: lazyPinConversationMutationWeb,
        pinItem: lazyPinItemMutationWeb,
        renameCalendarGroup: lazyRenameCalendarGroupMutationWeb,
        renameFolder: lazyRenameFolderWeb,
        respondToMeetingInvite: lazyRespondToMeetingInviteMutationWeb,
        saveDraft: lazySaveDraftWeb,
        saveSmartReply: lazySaveSmartReplyWeb,
        scheduleConversation: lazyScheduleConversationWeb,
        sendDraft: lazySendDraftWeb,
        sendSmartReply: lazySendSmartReplyWeb,
        setInferenceClassificationOnConversation: lazySetInferenceClassificationOnConversationWeb,
        updateFolderPermissions: lazyUpdateFolderPermissionsWeb,
        undoDeleteConversation: lazyUndoDeleteConversationWeb,
        undoMarkItemAsJunk: lazyUndoMarkItemAsJunkWeb,
        undoMoveConversation: lazyUndoMoveConversationWeb,
        undoScheduleConversation: lazyUndoScheduleConversationWeb,
        unsubscribeEmail: lazyUnsubscribeEmailWeb,
        updateLaunchTilePinStatus: lazyUpdateLaunchTilePinStatusMutationWeb,
        updateUserConfiguration: lazyUpdateUserConfigurationWeb,
    },

    /**
     * The root subscription type. Resolvers under subscriptions return an event stream
     * that the client responds to.
     *
     * For for definition and rationale, see https://graphql.org/blog/subscriptions-in-graphql-and-relay/#event-based-subscriptions
     * For resolver implementation, see https://www.apollographql.com/docs/apollo-server/data/subscriptions
     * For client consumption, see https://www.apollographql.com/docs/react/data/subscriptions
     */
    Subscription: {
        subscribeToHierarchyNotifications: lazySubscribeToHierarchyNotificationsWeb,
        subscribeToRowNotifications: lazySubscribeToRowNotificationsWeb,
        subscribeToNotificationChannelReady: lazySubscribeToNotificationChannelReadySubscriptionWeb,
    },

    /* ============================ */
    /* Resolvers for non-root types */
    /* ============================ */

    Conversation: {
        conversationNodes: lazyConversationConversationNodesWebResolver,
    },
};
